import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from '../services/api';

const FrontendUiSettingsPage: React.FC = () => {
  const [localeSwitcherHidden, setLocaleSwitcherHidden] = React.useState(false);
  const [emailVerificationDisabled, setEmailVerificationDisabled] = React.useState(false);
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['frontend-ui-config'],
    queryFn: () => apiService.getFrontendUiConfig(),
  });

  React.useEffect(() => {
    if (!data?.config) return;
    setLocaleSwitcherHidden(Boolean(data.config.locale_switcher_hidden));
    setEmailVerificationDisabled(Boolean(data.config.email_verification_disabled));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiService.updateFrontendUiConfig({
        locale_switcher_hidden: localeSwitcherHidden,
        email_verification_disabled: emailVerificationDisabled,
      }),
    onSuccess: (res) => {
      setSaveMessage(res.message || 'Réglages enregistrés.');
      setTimeout(() => setSaveMessage(null), 2200);
      void refetch();
    },
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Interface site public
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Options d&apos;affichage et d&apos;inscription du site Sherlook (frontend Next.js).
      </Typography>

      {error && <Alert severity="error">Impossible de charger la configuration.</Alert>}
      {saveMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {saveMessage}
        </Alert>
      )}

      <Card sx={{ maxWidth: 720, mb: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={localeSwitcherHidden}
                onChange={(e) => setLocaleSwitcherHidden(e.target.checked)}
                disabled={isLoading || saveMutation.isPending}
              />
            }
            label="Masquer le menu de langue (Français / English) dans la navbar"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Lorsque cette option est activée, le sélecteur de langue n&apos;apparaît plus sur le site
            public (desktop, mobile et menu burger). Les URLs /fr/ et /en/ restent accessibles
            directement.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ maxWidth: 720 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={emailVerificationDisabled}
                onChange={(e) => setEmailVerificationDisabled(e.target.checked)}
                disabled={isLoading || saveMutation.isPending}
                color="warning"
              />
            }
            label="Mode panne email (contournement temporaire)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Lorsque cette option est activée :
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, color: 'text.secondary', typography: 'body2' }}>
            <li>Inscription sans vérification email — comptes créés déjà actifs</li>
            <li>Connexion possible immédiatement (pas de message « vérifiez votre mail »)</li>
            <li>
              Mot de passe oublié : le site demande d’écrire à <strong>contact@sherlook.fr</strong> (avec
              l’email du compte) — vous changez le mot de passe dans l’admin
            </li>
          </Box>
          {emailVerificationDisabled ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Mode panne email actif. Désactivez-le dès que la délivrabilité SMTP est rétablie.
            </Alert>
          ) : null}
          <Box mt={3}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || isLoading}
            >
              Enregistrer
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FrontendUiSettingsPage;
